import {
  BookOpenCheck,
  Bug,
  ChevronDown,
  CircleHelp,
  Mail,
  Menu,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { AccountMenu } from "./components/AccountMenu";
import { AnalyticsManager } from "./components/AnalyticsManager";
import { BrandMark } from "./components/BrandMark";
import { RoomActivityBridge } from "./components/RoomActivityBridge";
import { SeoManager } from "./components/SeoManager";
import { SupportDialog } from "./components/SupportDialog";
import {
  BlogIndexPage,
  BotArticlePage,
  BotExperimentsPage,
  ExperimentPage,
  SmartLudoDicePage,
} from "./pages/BlogPages";
import { DashboardPage } from "./pages/DashboardPage";
import { GamesPage } from "./pages/GamesPage";
import { GameTutorialPage } from "./pages/GameTutorialPage";
import {
  AboutPage,
  BillingPage,
  ContactPage,
  CookiesPage,
  PrivacyPage,
  TermsPage,
} from "./pages/InfoPages";
import { JoinPage } from "./pages/JoinPage";
import { LandingPage } from "./pages/LandingPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { ParentalConsentPage } from "./pages/ParentalConsentPage";
import { RoomPage } from "./pages/RoomPage";
import { SafetyPage } from "./pages/SafetyPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SignInPage } from "./pages/SignInPage";
import { TutorialsPage } from "./pages/TutorialsPage";
import { useAuthSession } from "./useAuthSession";
import { useVisitorContext } from "./visitorContext";

const routeScrollPositions = new Map<string, { left: number; top: number }>();

function RouteScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    const pathChanged = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;
    let anchorFrame: number | undefined;

    if (location.hash) {
      anchorFrame = window.requestAnimationFrame(() => {
        const anchor = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        anchor?.scrollIntoView();
      });
    } else if (navigationType === "POP") {
      const savedPosition = routeScrollPositions.get(location.key);
      window.scrollTo(savedPosition ?? { left: 0, top: 0 });
    } else if (pathChanged) {
      window.scrollTo({ left: 0, top: 0 });
    }

    return () => {
      if (anchorFrame !== undefined) window.cancelAnimationFrame(anchorFrame);
      routeScrollPositions.set(location.key, {
        left: window.scrollX,
        top: window.scrollY,
      });
    };
  }, [location.hash, location.key, location.pathname, navigationType]);

  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportKind, setSupportKind] = useState<"feedback" | "issue" | null>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const { session } = useAuthSession();

  useEffect(() => {
    if (!helpOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!helpMenuRef.current?.contains(event.target as Node)) setHelpOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setHelpOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [helpOpen]);

  const closeNavigation = () => {
    setOpen(false);
    setHelpOpen(false);
  };

  return (
    <>
      <header className="site-header">
        <Link className="brand" to="/">
          <BrandMark className="brand-lockup" variant="lockup-light" labelled />
        </Link>
        <button
          type="button"
          className="icon-button menu-toggle"
          aria-label="Toggle navigation"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
        <nav className={open ? "nav open" : "nav"}>
          <Link className="nav-help-link" to="/blog" onClick={closeNavigation}>
            <BookOpenCheck /> Blog
          </Link>
          <div className="nav-help" ref={helpMenuRef}>
            <button
              type="button"
              className="nav-help-link"
              aria-haspopup="menu"
              aria-expanded={helpOpen}
              onClick={() => setHelpOpen((value) => !value)}
            >
              <CircleHelp /> Help <ChevronDown className={helpOpen ? "open" : ""} />
            </button>
            {helpOpen ? (
              <div className="nav-help-popover" role="menu" aria-label="Help options">
                <Link to="/how-to-play" role="menuitem" onClick={closeNavigation}>
                  <BookOpenCheck />
                  <span>
                    <strong>Game guides</strong>
                    <small>Rules and guided practice</small>
                  </span>
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeNavigation();
                    setSupportKind("issue");
                  }}
                >
                  <Bug />
                  <span>
                    <strong>Report issue</strong>
                    <small>Tell us what is not working</small>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeNavigation();
                    setSupportKind("feedback");
                  }}
                >
                  <MessageSquareText />
                  <span>
                    <strong>Send feedback</strong>
                    <small>Share an idea or request</small>
                  </span>
                </button>
                <Link to="/contact" role="menuitem" onClick={closeNavigation}>
                  <Mail />
                  <span>
                    <strong>Contact</strong>
                    <small>Account, business, or press help</small>
                  </span>
                </Link>
                <Link to="/safety" role="menuitem" onClick={closeNavigation}>
                  <ShieldCheck />
                  <span>
                    <strong>Safety</strong>
                    <small>Controls, reporting, and families</small>
                  </span>
                </Link>
              </div>
            ) : null}
          </div>
          <AccountMenu onNavigate={() => setOpen(false)} />
          {session && (
            <Link
              className="button button-primary compact"
              to="/app"
              onClick={() => setOpen(false)}
            >
              <Sparkles size={16} /> Start a room
            </Link>
          )}
        </nav>
      </header>
      {supportKind ? (
        <SupportDialog
          kind={supportKind}
          context={{ source: "header" }}
          onClose={() => setSupportKind(null)}
        />
      ) : null}
    </>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <Link className="brand" to="/">
          <BrandMark className="brand-lockup" variant="lockup-light" labelled />
        </Link>
        <p>Gather. Play. Chat. — with friends and family.</p>
        <small>
          © 2026 Topzle by{" "}
          <a href="https://azonova.com" target="_blank" rel="noreferrer">
            Azonova
          </a>
          .
        </small>
      </div>
      <div className="footer-links footer-links-simple">
        <Link to="/games">Games</Link>
        <Link to="/blog">Blog</Link>
        <Link to="/about">About</Link>
        <Link to="/contact">Contact</Link>
        <Link to="/safety">Safety</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/cookies">Cookies</Link>
        <a href="mailto:contact@topzle.com">contact@topzle.com</a>
      </div>
    </footer>
  );
}

export function App() {
  useVisitorContext();
  const location = useLocation();
  const isRoom = location.pathname.startsWith("/room/");
  const magicLinkSearch = new URLSearchParams(location.search);
  const isMagicLink =
    location.pathname === "/sign-in" &&
    magicLinkSearch.get("audience") === "player" &&
    Boolean(magicLinkSearch.get("challenge")) &&
    Boolean(magicLinkSearch.get("token"));
  const hideSiteChrome = isRoom || isMagicLink;
  return (
    <>
      <AnalyticsManager />
      <RouteScrollManager />
      <SeoManager />
      <RoomActivityBridge />
      {!hideSiteChrome && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/bots/:gameId" element={<BotArticlePage />} />
          <Route path="/blog/experiments/:gameId" element={<ExperimentPage />} />
          <Route path="/blog/bot-experiments" element={<BotExperimentsPage />} />
          <Route path="/blog/smart-ludo-dice" element={<SmartLudoDicePage />} />
          <Route path="/how-to-play" element={<TutorialsPage />} />
          <Route path="/how-to-play/:gameId" element={<GameTutorialPage />} />
          <Route path="/safety" element={<SafetyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/join/:code" element={<JoinPage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignInPage initialMode="signup" />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/parental-consent" element={<ParentalConsentPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/app" element={<DashboardPage />} />
          <Route path="/room/:roomId" element={<RoomPage />} />
          <Route
            path="*"
            element={
              <section className="empty-state">
                <span className="eyebrow">404</span>
                <h1>This path wandered out of the playground.</h1>
                <Link className="button button-primary" to="/">
                  Back to Topzle
                </Link>
              </section>
            }
          />
        </Routes>
      </main>
      {!hideSiteChrome && <Footer />}
    </>
  );
}
